/**
 * Remote session orchestration.
 *
 * The browser can never hole-punch (no raw UDP/TCP), so every session is
 * relayed. The full sequence:
 *
 *   1. ws -> /ws/rendezvous, send PunchHoleRequest{force_relay: true}
 *   2. hbbs forwards PunchHole to the peer; the peer opens a relay and replies
 *      with RelayResponse{uuid, relay_server, pk} back through the SAME ws
 *      connection, so it must stay open until we have it.
 *   3. ws -> /ws/relay, send RequestRelay{uuid} to be paired with the peer
 *   4. peer sends SignedId; verify it, send PublicKey, enable encryption
 *   5. peer sends Hash{salt, challenge}; reply with LoginRequest
 *   6. peer sends LoginResponse{peer_info} and the stream begins
 */
import { WsTransport } from "./transport.js";
import { encodePunchHoleRequest, encodeRequestRelay, decodeRendezvous, parseIdPk, ConnType, } from "./rendezvous.js";
import { decodeMessage, encodePublicKey, encodeLoginRequest, encodeTestDelay, encodeMouseEvent, encodeKeyEvent, encodeClipboardText, encodeRefreshVideo, encodeSwitchDisplay, encodeOptionUpdate, encodeAuth2FA, encodeCloseReason, encodeElevationDirect, encodeElevationWithLogon, encodeScreenshotRequest, encodeVoiceCallRequest, encodeCaptureDisplays, encodeTextAsKeystrokes, encodeChat, encodeAudioFrame, encodeTogglePrivacyMode, encodeToggleVirtualDisplay, encodeSelectedSid, encodeChangeResolution, encodeAutoAdjustFps, encodeFollowCurrentDisplay, encodeRestartRemote, encodeStopService, encodeTouchPan, encodeTouchScale, encodeSwitchBack, ClipboardFormat, ImageQuality, PreferCodec, MISC, parseCloseReason, parseChatMessage, parseAudioFormat, parseBackNotification, PrivacyModeState, BlockInputState, parsePermissionInfo, PERMISSION_NAMES, } from "./message.js";
import { decompress as zstdDecompress } from "fzstd";
import { signOpen, createSymmetricKeyMsg, fromBase64, hashPasswordForLogin, hashStoredPasswordForLogin, } from "./crypto.js";
const VERSION = "1.5.0";
/** MouseEvent.mask layout, from the client's input handling. */
export const MouseMask = {
    Move: 0,
    LeftDown: 1 | (0 << 3),
    LeftUp: 2 | (0 << 3),
    RightDown: 1 | (1 << 3),
    RightUp: 2 | (1 << 3),
    MiddleDown: 1 | (2 << 3),
    MiddleUp: 2 | (2 << 3),
    Wheel: 3,
    Trackpad: 4,
};
export class RemoteSession {
    #cfg;
    #events;
    #rendezvous = null;
    #relay = null;
    #state = "idle";
    #sessionId;
    #peerInfo = null;
    #lastHash = null;
    #pumpAbort = false;
    #keepAlive = null;
    #lastNetworkDelay = 0;
    /** The last frame rate reported to the machine, so it is not resent. */
    #reportedFps = 0;
    /**
     * What the machine currently allows. Everything starts permitted: the
     * machine announces only what it takes away or gives back.
     */
    #permissions = {
        keyboard: true, clipboard: true, audio: true, file: true,
        restart: true, recording: true, blockInput: true, privacyMode: true,
    };
    #lastTargetBitrate = 0;
    constructor(cfg, events = {}) {
        this.#cfg = cfg;
        this.#events = events;
        // A random non-zero session id; the peer uses it to correlate windows.
        const buf = new Uint32Array(2);
        crypto.getRandomValues(buf);
        this.#sessionId = (BigInt(buf[0]) << 32n) | BigInt(buf[1]);
    }
    /** What the machine currently allows. */
    get permissions() {
        return this.#permissions;
    }
    get state() {
        return this.#state;
    }
    get peerInfo() {
        return this.#peerInfo;
    }
    get secured() {
        return this.#relay?.secured ?? false;
    }
    /**
     * The peer's password salt, once it has sent one.
     *
     * `sha256(password || salt)` is the value an address book stores as `hash`,
     * which is what lets a later connection authenticate without the password
     * itself ever being kept.
     */
    /** Round-trip delay in ms, as the peer last measured it. */
    get networkDelay() {
        return this.#lastNetworkDelay;
    }
    /** The bitrate the peer's quality controller is currently targeting. */
    get targetBitrate() {
        return this.#lastTargetBitrate;
    }
    get salt() {
        return this.#lastHash?.salt ?? "";
    }
    /** True once the peer has asked for credentials. */
    get awaitingLogin() {
        return this.#lastHash !== null;
    }
    #setState(s, detail) {
        this.#state = s;
        this.#events.onState?.(s, detail);
    }
    #wsUrl(path, params) {
        const u = new URL(this.#cfg.origin);
        u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
        u.pathname = path;
        u.search = "";
        for (const [k, v] of Object.entries(params)) {
            if (v)
                u.searchParams.set(k, v);
        }
        return u.toString();
    }
    /** Runs the whole connect sequence. Rejects with a displayable message. */
    async connect() {
        try {
            const relayInfo = await this.#rendezvousPhase();
            await this.#relayPhase(relayInfo);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            this.#setState("closed", message);
            this.#events.onError?.(message);
            this.close(message);
            throw err;
        }
    }
    /** Steps 1-2: ask hbbs to broker a relay with the peer. */
    async #rendezvousPhase() {
        this.#setState("rendezvous");
        const ws = new WsTransport(this.#wsUrl("/ws/rendezvous", { token: this.#cfg.token, peer: this.#cfg.peerId }));
        this.#rendezvous = ws;
        await ws.connect();
        ws.send(encodePunchHoleRequest({
            id: this.#cfg.peerId,
            licenceKey: this.#cfg.serverKey,
            connType: connTypeValue(this.#cfg.connType),
            token: this.#cfg.token,
            version: VERSION,
            // A browser has no listening socket, so a relay is the only option.
            forceRelay: true,
        }));
        this.#setState("waiting-peer");
        // The peer has to be reached over UDP and open its own relay leg, so allow
        // generous time before declaring it unreachable.
        const deadline = Date.now() + 30_000;
        for (;;) {
            const remaining = deadline - Date.now();
            if (remaining <= 0)
                throw new Error("The device did not respond");
            const frame = await ws.recv(remaining);
            if (!frame)
                throw new Error(ws.closeReason || "Rendezvous connection closed");
            const msg = decodeRendezvous(frame);
            if (msg.kind === "punch_hole_response") {
                // Any response on this path is a refusal: a browser never punches.
                const reason = msg.value.otherFailure || failureText(msg.value.failure ?? 0);
                throw new Error(reason);
            }
            if (msg.kind === "relay_response") {
                const v = msg.value;
                if (v.refuseReason)
                    throw new Error(v.refuseReason);
                if (!v.uuid)
                    continue;
                const peerSignPk = this.#verifyPeerKey(v.pk);
                return {
                    uuid: v.uuid,
                    relayServer: v.relayServer,
                    peerSignPk,
                };
            }
            // Anything else (config updates etc.) is not interesting here.
        }
    }
    /**
     * Verifies the rendezvous server's signature over IdPk and returns the
     * peer's signing key. Without a configured server key we cannot authenticate
     * the peer at all, and the session proceeds unencrypted - the same fallback
     * the desktop client makes for key-less deployments.
     */
    #verifyPeerKey(signedPk) {
        if (!signedPk || signedPk.length === 0)
            return new Uint8Array();
        if (!this.#cfg.serverKey)
            return new Uint8Array();
        let serverPk;
        try {
            serverPk = fromBase64(this.#cfg.serverKey);
        }
        catch {
            return new Uint8Array();
        }
        const payload = signOpen(signedPk, serverPk);
        if (!payload) {
            throw new Error("The server's signature on this device's key is invalid");
        }
        const idpk = parseIdPk(payload);
        if (idpk.id !== this.#cfg.peerId) {
            throw new Error("The server returned a key for a different device");
        }
        return idpk.pk;
    }
    /** Steps 3-6: pair through the relay, handshake and log in. */
    async #relayPhase(info) {
        this.#setState("relay");
        const ws = new WsTransport(this.#wsUrl("/ws/relay", {
            token: this.#cfg.token,
            peer: this.#cfg.peerId,
            relay: info.relayServer,
        }));
        this.#relay = ws;
        await ws.connect();
        ws.send(encodeRequestRelay({
            id: this.#cfg.peerId,
            uuid: info.uuid,
            licenceKey: this.#cfg.serverKey,
            connType: connTypeValue(this.#cfg.connType),
            token: this.#cfg.token,
            secure: info.peerSignPk.length > 0,
        }));
        // The rendezvous leg has done its job.
        this.#rendezvous?.close("relay established");
        this.#rendezvous = null;
        this.#setState("handshake");
        await this.#handshake(ws, info.peerSignPk);
        this.#pump(ws);
    }
    /** Step 4: authenticate the peer and switch the stream to encrypted. */
    async #handshake(ws, peerSignPk) {
        const frame = await ws.recv(20_000);
        if (!frame)
            throw new Error(ws.closeReason || "The device did not complete the handshake");
        const msg = decodeMessage(frame);
        if (msg.kind !== "signed_id") {
            // Some peers open with Hash when no key is configured; handle it.
            if (msg.kind === "hash") {
                this.#lastHash = { salt: msg.salt, challenge: msg.challenge };
                await this.#sendLogin();
                return;
            }
            throw new Error("Unexpected handshake message from the device");
        }
        if (peerSignPk.length === 0) {
            // No trusted identity: mirror the desktop client and continue without
            // encryption rather than failing the connection outright.
            ws.send(encodePublicKey(new Uint8Array(), new Uint8Array()));
            return;
        }
        const payload = signOpen(msg.id, peerSignPk);
        if (!payload)
            throw new Error("Could not verify the device's identity");
        const idpk = parseIdPk(payload);
        if (idpk.id !== this.#cfg.peerId) {
            throw new Error("The device identified itself as a different machine");
        }
        if (idpk.pk.length !== 32)
            throw new Error("The device sent a malformed key");
        const km = createSymmetricKeyMsg(idpk.pk);
        ws.send(encodePublicKey(km.asymmetric, km.symmetric));
        // Everything from here on is encrypted, in both directions.
        ws.setKey(km.key);
    }
    /** Steps 5-6 and the steady-state read loop. */
    async #pump(ws) {
        this.#startKeepAlive();
        while (!this.#pumpAbort) {
            const frame = await ws.recv();
            if (!frame)
                break;
            let msg;
            try {
                msg = decodeMessage(frame);
            }
            catch {
                continue; // a malformed frame is not fatal
            }
            await this.#handle(msg);
        }
        this.#stopKeepAlive();
        if (this.#state !== "closed") {
            const reason = ws.closeReason || "The connection was closed";
            this.#setState("closed", reason);
            this.#events.onClose?.(reason);
        }
    }
    async #handle(msg) {
        switch (msg.kind) {
            case "hash":
                this.#lastHash = { salt: msg.salt, challenge: msg.challenge };
                this.#setState("authenticating");
                await this.#sendLogin();
                return;
            case "login_response":
                if (msg.error) {
                    this.#onLoginError(msg.error);
                    return;
                }
                if (msg.peerInfo) {
                    this.#peerInfo = msg.peerInfo;
                    this.#setState("connected");
                    this.#events.onPeerInfo?.(msg.peerInfo);
                    this.#confirmWindowsSession(msg.peerInfo);
                }
                break;
            case "peer_info":
                // A later PeerInfo is a *change* notification - the peer sends one
                // when its displays change, carrying only the displays and leaving
                // identity and capabilities empty. Replacing wholesale would throw
                // away what the machine said it could do at login.
                this.#peerInfo = mergePeerInfo(this.#peerInfo, msg.peerInfo);
                this.#events.onPeerInfo?.(this.#peerInfo);
                break;
            case "test_delay":
                // Echo it back UNCHANGED - in particular, leave from_client false.
                //
                // The peer uses that flag to tell its own probe's reply (false) from a
                // probe the client started (true). Setting it true makes the peer
                // merely bounce the message back and never clear its last_test_delay,
                // so the delay it measures grows without bound and its QoS clamps the
                // frame rate to the 1 fps floor. This is what "low fps" was.
                if (!msg.fromClient) {
                    this.#relay?.send(encodeTestDelay(msg.time, false, msg.lastDelay, msg.targetBitrate));
                    this.#lastNetworkDelay = msg.lastDelay;
                    this.#lastTargetBitrate = msg.targetBitrate;
                }
                break;
            case "misc":
                if (msg.field === MISC.elevation_response) {
                    const text = new TextDecoder().decode(msg.raw);
                    // The peer sends an empty string on success and an error otherwise.
                    this.#events.onElevation?.({ ok: text.length === 0, message: text });
                    break;
                }
                if (msg.field === MISC.uac) {
                    this.#events.onElevationState?.({ uac: msg.raw[0] === 1 });
                    break;
                }
                if (msg.field === MISC.foreground_window_elevated) {
                    this.#events.onElevationState?.({ elevated: msg.raw[0] === 1 });
                    break;
                }
                if (msg.field === MISC.permission_info) {
                    const { permission, enabled } = parsePermissionInfo(msg.raw);
                    const name = PERMISSION_NAMES[permission];
                    if (name) {
                        this.#permissions = { ...this.#permissions, [name]: enabled };
                        this.#events.onPermissions?.(this.#permissions);
                    }
                    break;
                }
                if (msg.field === MISC.back_notification) {
                    this.#onBackNotification(parseBackNotification(msg.raw));
                    break;
                }
                if (msg.field === MISC.chat_message) {
                    const text = parseChatMessage(msg.raw);
                    if (text)
                        this.#events.onChat?.(text);
                    break;
                }
                if (msg.field === MISC.audio_format) {
                    this.#events.onAudioFormat?.(parseAudioFormat(msg.raw));
                    break;
                }
                if (msg.field === MISC.close_reason) {
                    const reason = parseCloseReason(msg.raw);
                    this.#setState("closed", reason);
                    this.#events.onClose?.(reason);
                    this.close(reason);
                    return;
                }
                break;
            case "clipboard": {
                for (const entry of msg.clipboards) {
                    let content = entry.content;
                    if (entry.compress) {
                        try {
                            content = zstdDecompress(content);
                        }
                        catch {
                            continue; // unreadable; dropping beats pasting rubbish
                        }
                    }
                    if (entry.format === ClipboardFormat.ImagePng
                        || entry.format === ClipboardFormat.ImageSvg) {
                        this.#events.onClipboardImage?.({ bytes: content, format: entry.format });
                    }
                    else if (entry.format === ClipboardFormat.Text) {
                        this.#events.onClipboard?.(new TextDecoder().decode(content));
                    }
                }
                break;
            }
            case "screenshot":
                this.#events.onScreenshot?.({
                    ok: msg.message.length === 0 && msg.data.length > 0,
                    message: msg.message,
                    data: msg.data,
                });
                break;
            case "voice_call_response":
                this.#events.onVoiceCall?.(msg.accepted);
                break;
            case "audio_frame":
                this.#events.onAudio?.(msg.data);
                break;
            case "switch_sides_request":
                // A browser has no screen of its own to hand over. Say so rather than
                // leaving the machine waiting for an answer that cannot come.
                this.#events.onSwitchSidesRequest?.();
                this.#relay?.send(encodeSwitchBack());
                break;
            case "message_box":
                // The peer surfaces auth problems this way too.
                this.#events.onError?.(msg.text || msg.title);
                break;
            default:
                break;
        }
        this.#events.onMessage?.(msg);
    }
    #onLoginError(error) {
        const lower = error.toLowerCase();
        if (lower.includes("wrong password") || lower.includes("wrong_password")) {
            this.#events.onPasswordRequired?.(true);
            return;
        }
        if (lower.includes("no password") || lower.includes("password is required")) {
            this.#events.onPasswordRequired?.(false);
            return;
        }
        if (lower.includes("2fa") || lower.includes("two-factor")) {
            this.#events.onTwoFactorRequired?.();
            return;
        }
        this.#events.onError?.(error);
        this.close(error);
    }
    async #sendLogin() {
        const hash = this.#lastHash;
        if (!hash)
            return;
        let password = new Uint8Array();
        if (this.#cfg.passwordHash) {
            password = await hashStoredPasswordForLogin(this.#cfg.passwordHash, hash.challenge);
        }
        else if (this.#cfg.password) {
            password = await hashPasswordForLogin(this.#cfg.password, hash.salt, hash.challenge);
        }
        // With no password the peer prompts its own user to accept, which is a
        // legitimate way to connect.
        this.#relay?.send(encodeLoginRequest({
            // NOT the operator's name: the controlled peer checks this against its
            // own id and answers "Offline" if it does not match (see the
            // is_direct_ip_access check in the client's server/connection.rs).
            // The operator is identified by myId/myName instead.
            username: this.#cfg.peerId,
            password,
            myId: this.#cfg.myId ?? "web",
            myName: this.#cfg.myName ?? "Web Console",
            sessionId: this.#sessionId,
            version: VERSION,
            myPlatform: "Web",
            connType: this.#cfg.connType,
            osUsername: this.#cfg.osUsername,
            osPassword: this.#cfg.osPassword,
            fileTransferDir: this.#cfg.fileTransferDir,
            showHidden: this.#cfg.showHidden,
            videoAckRequired: false,
            options: this.#cfg.options ?? defaultOptions(),
        }));
    }
    /**
     * Re-authenticates with a password the user has just typed.
     *
     * Valid at any point after the peer has sent its Hash, not only after a
     * rejection: a peer in "accept" mode never rejects, it simply waits, so the
     * user must be able to offer a password unprompted.
     */
    async retryWithPassword(password) {
        this.#cfg = { ...this.#cfg, password, passwordHash: undefined };
        await this.#sendLogin();
    }
    /** Re-authenticates with a stored hash rather than a password. */
    async retryWithHash(hash) {
        this.#cfg = { ...this.#cfg, password: undefined, passwordHash: hash };
        await this.#sendLogin();
    }
    submitTwoFactor(code) {
        this.#relay?.send(encodeAuth2FA(code));
    }
    #startKeepAlive() {
        this.#stopKeepAlive();
        // The peer drops idle connections; an empty Message is the cheapest ping
        // and is explicitly passed through the cipher unencrypted.
        this.#keepAlive = setInterval(() => {
            this.#relay?.send(new Uint8Array());
        }, 15_000);
    }
    #stopKeepAlive() {
        if (this.#keepAlive) {
            clearInterval(this.#keepAlive);
            this.#keepAlive = null;
        }
    }
    // ---- outbound control ---------------------------------------------------
    sendMouse(e) {
        this.#relay?.send(encodeMouseEvent(e));
    }
    sendKey(e) {
        this.#relay?.send(encodeKeyEvent(e));
    }
    sendClipboardText(text) {
        this.#relay?.send(encodeClipboardText(text));
    }
    refreshVideo() {
        this.#relay?.send(encodeRefreshVideo());
    }
    /**
     * Shows a different screen.
     *
     * Width and height stay zero. The peer reads a non-zero size as "change this
     * display's resolution to that", so sending the screen's own size makes
     * every switch reconfigure the machine's display - the capture restarts, the
     * picture flickers, and on a virtual display it can stop altogether.
     *
     * An index the peer never reported is ignored rather than sent: switching to
     * a screen that is not there leaves the session with no video at all.
     */
    switchDisplay(index) {
        const displays = this.#peerInfo?.displays;
        if (displays && (index < 0 || index >= displays.length))
            return;
        this.#relay?.send(encodeSwitchDisplay(index, 0, 0));
        // Follow it with the capture set, as the desktop client does.
        this.#relay?.send(encodeCaptureDisplays({ set: [index] }));
    }
    /**
     * Sets a screen's resolution on the machine.
     *
     * Its own message, not a switch carrying a size: conflating the two made
     * every screen change reconfigure the machine's display.
     */
    setDisplayResolution(index, width, height) {
        this.#relay?.send(encodeChangeResolution(index, width, height));
    }
    /**
     * Tells the machine what frame rate this client can keep up with.
     *
     * The machine caps what it encodes to this. Without it, it is left guessing
     * from network delay alone - which is why a client that never reports gets
     * whatever default the machine happens to have.
     */
    reportDecodeRate(fps) {
        const clamped = Math.max(5, Math.min(120, Math.round(fps)));
        if (clamped === this.#reportedFps)
            return;
        this.#reportedFps = clamped;
        this.#relay?.send(encodeAutoAdjustFps(clamped));
    }
    /** Restarts the machine. */
    restartMachine() {
        this.#relay?.send(encodeRestartRemote());
    }
    /**
     * Stops RustDesk on the machine.
     *
     * Every session on it ends, including this one, and nobody can connect again
     * until someone starts it there.
     */
    stopRemoteService() {
        this.#relay?.send(encodeStopService());
    }
    /** A two-finger pan, from a trackpad or touchscreen. */
    sendTouchPan(phase, x, y) {
        this.#relay?.send(encodeTouchPan(phase, Math.round(x), Math.round(y)));
    }
    /** A pinch, as the change since the last one. Zero ends the gesture. */
    sendTouchScale(deltaScale) {
        this.#relay?.send(encodeTouchScale(deltaScale));
    }
    /** Follow whichever screen the person at the machine moves to. */
    followCurrentDisplay(on) {
        this.#relay?.send(encodeFollowCurrentDisplay(on ? 1 : 0));
    }
    updateOptions(options) {
        this.#relay?.send(encodeOptionUpdate(options));
    }
    /** Raises a UAC prompt on the machine for someone there to accept. */
    requestElevation() {
        this.#relay?.send(encodeElevationDirect());
    }
    /** Elevates with administrator credentials, without prompting at the machine. */
    requestElevationWithLogon(username, password) {
        this.#relay?.send(encodeElevationWithLogon(username, password));
    }
    /** Asks the machine for a still of a display. */
    requestScreenshot(display, sid = "") {
        this.#relay?.send(encodeScreenshotRequest(display, sid));
    }
    /**
     * Types text one key at a time.
     *
     * Some remote fields ignore a clipboard paste - UAC prompts, RDP sessions,
     * VM consoles - and only accept real key presses.
     */
    typeText(text) {
        for (const payload of encodeTextAsKeystrokes(text)) {
            this.#relay?.send(payload);
        }
    }
    sendChat(text) {
        this.#relay?.send(encodeChat(text));
    }
    /** Starts or ends a voice call. */
    setVoiceCall(connect) {
        this.#relay?.send(encodeVoiceCallRequest(connect, BigInt(Date.now())));
    }
    /** Sends one encoded audio frame from the microphone. */
    sendAudio(data) {
        this.#relay?.send(encodeAudioFrame(data));
    }
    /**
     * Chooses which displays the machine captures; several means all at once.
     *
     * Indexes the peer never reported are dropped: asking for a screen that is
     * not there leaves the machine capturing nothing.
     */
    captureDisplays(displays) {
        const count = this.#peerInfo?.displays.length ?? 0;
        const valid = count ? displays.filter((d) => d >= 0 && d < count) : displays;
        if (!valid.length)
            return;
        this.#relay?.send(encodeCaptureDisplays({ set: valid }));
    }
    /** Locks out the keyboard and mouse of whoever is at the machine. */
    setBlockInput(blocked) {
        this.#relay?.send(encodeOptionUpdate({ blockInput: blocked }));
    }
    /**
     * Blanks the machine's own screen.
     *
     * `implKey` picks one of the ways the machine offers (see
     * `peerCapabilities`); an empty key lets it choose. The result comes back
     * through `onPrivacyMode`, because it can fail for reasons only the machine
     * knows - no driver, or someone else already has it on.
     */
    setPrivacyMode(on, implKey = "") {
        this.#relay?.send(encodeTogglePrivacyMode(implKey, on));
    }
    /**
     * Adds or removes a virtual display.
     *
     * What a machine with no real graphics adapter needs: its basic display
     * driver often runs at a very low refresh rate, and no client can show more
     * frames than the machine produces.
     */
    setVirtualDisplay(display, on) {
        this.#relay?.send(encodeToggleVirtualDisplay(display, on));
    }
    /**
     * Turns the peer's verdict on privacy mode or block input into something a
     * person can read. Both can fail for reasons only the machine knows.
     */
    #onBackNotification(n) {
        if (n.privacyModeState !== undefined) {
            const S = PrivacyModeState;
            const map = {
                [S.OnSucceeded]: { on: true, ok: true, message: "" },
                [S.OffSucceeded]: { on: false, ok: true, message: "" },
                [S.OffByPeer]: { on: false, ok: true, message: "Someone at the machine turned it off." },
                [S.OnByOther]: { on: false, ok: false, message: "Another session already has it on." },
                [S.NotSupported]: {
                    on: false, ok: false,
                    message: "This machine cannot blank its screen. It needs a display driver that supports it.",
                },
                [S.OnFailedDenied]: {
                    on: false, ok: false, message: "The machine refused: blanking its screen is not permitted.",
                },
                [S.OnFailedPlugin]: {
                    on: false, ok: false, message: "The machine is missing the driver this needs.",
                },
                [S.OnFailed]: { on: false, ok: false, message: "The machine could not blank its screen." },
                [S.OffFailed]: { on: true, ok: false, message: "The machine could not restore its screen." },
            };
            const r = map[n.privacyModeState] ?? {
                on: false, ok: false, message: "The machine gave no reason.",
            };
            this.#events.onPrivacyMode?.({
                ...r,
                message: n.details ? `${r.message} ${n.details}`.trim() : r.message,
            });
            return;
        }
        if (n.blockInputState !== undefined) {
            const B = BlockInputState;
            const map = {
                [B.OnSucceeded]: { blocked: true, ok: true, message: "" },
                [B.OffSucceeded]: { blocked: false, ok: true, message: "" },
                [B.OnFailed]: {
                    blocked: false, ok: false,
                    message: "The machine could not lock out its keyboard and mouse.",
                },
                [B.OffFailed]: {
                    blocked: true, ok: false,
                    message: "The machine could not unlock its keyboard and mouse.",
                },
            };
            const r = map[n.blockInputState];
            if (r)
                this.#events.onBlockInput?.(r);
        }
    }
    /**
     * Answers a machine that is waiting to be told which Windows session to use.
     *
     * A choice already made is confirmed without asking; so is a machine that is
     * already in the only session on offer. Anything else has to come from the
     * person, because picking the wrong session moves the machine's console to
     * someone else's desktop.
     */
    #confirmWindowsSession(info) {
        const { sessions, currentSid } = info.windowsSessions;
        if (!sessions.length)
            return;
        const wanted = this.#cfg.windowsSessionId;
        if (wanted !== undefined && sessions.some((s) => s.sid === wanted)) {
            this.selectWindowsSession(wanted);
            return;
        }
        if (sessions.length === 1 && sessions[0].sid === currentSid) {
            this.selectWindowsSession(currentSid);
            return;
        }
        this.#events.onWindowsSessionRequired?.({ sessions, currentSid });
    }
    /**
     * Chooses the Windows session to work in.
     *
     * Choosing one the machine is not already in makes it move there, which ends
     * this connection - reconnecting arrives in the chosen session.
     */
    selectWindowsSession(sid) {
        this.#relay?.send(encodeSelectedSid(sid));
    }
    /** Raw send, used by the file-transfer layer. */
    sendRaw(payload) {
        this.#relay?.send(payload);
    }
    close(reason = "closed by user") {
        if (this.#state === "closed" && this.#pumpAbort)
            return;
        this.#pumpAbort = true;
        this.#stopKeepAlive();
        try {
            this.#relay?.send(encodeCloseReason(reason));
        }
        catch {
            /* the socket may already be gone */
        }
        this.#relay?.close(reason);
        this.#rendezvous?.close(reason);
        this.#relay = null;
        this.#rendezvous = null;
        this.#setState("closed", reason);
    }
}
function connTypeValue(t) {
    switch (t) {
        case "file_transfer": return ConnType.FILE_TRANSFER;
        case "port_forward": return ConnType.PORT_FORWARD;
        case "view_camera": return ConnType.VIEW_CAMERA;
        case "terminal": return ConnType.TERMINAL;
        default: return ConnType.DEFAULT;
    }
}
function failureText(failure) {
    switch (failure) {
        case 0: return "That device is not registered with this server";
        case 2: return "The device is offline";
        case 3: return "Key mismatch - this client is not configured for this server";
        case 4: return "License overuse";
        default: return "The device could not be reached";
    }
}
/**
 * Defaults tuned for a browser: advertise only codecs WebCodecs can decode,
 * and prefer VP9, which every current browser supports without licensing
 * caveats and which the peer can always produce.
 */
export function defaultOptions() {
    return {
        imageQuality: ImageQuality.Balanced,
        // Without this the peer keeps its own default, which is conservative.
        customFps: 30,
        // The peer only sends cursor shapes when the controller asks for them.
        showRemoteCursor: true,
        disableAudio: true,
        enableFileTransfer: true,
        supportedDecoding: {
            vp8: true, vp9: true, av1: false, h264: true, h265: false,
            prefer: PreferCodec.VP9,
        },
    };
}
/**
 * Folds a PeerInfo update into what is already known.
 *
 * Displays and the current display always come from the update - that is what
 * it is for. Everything else is kept unless the update actually carries it,
 * because a change notification leaves those fields empty and they are how the
 * console knows what the machine can do.
 */
function mergePeerInfo(previous, update) {
    if (!previous)
        return update;
    return {
        ...previous,
        displays: update.displays.length ? update.displays : previous.displays,
        currentDisplay: update.currentDisplay,
        username: update.username || previous.username,
        hostname: update.hostname || previous.hostname,
        platform: update.platform || previous.platform,
        version: update.version || previous.version,
        platformAdditions: update.platformAdditions && update.platformAdditions !== "{}"
            ? mergeAdditions(previous.platformAdditions, update.platformAdditions)
            : previous.platformAdditions,
        features: update.features.privacyMode || update.features.terminal
            ? update.features
            : previous.features,
    };
}
/** Merges the machine's JSON extras, keeping keys the update does not mention. */
function mergeAdditions(previous, update) {
    try {
        const a = JSON.parse(previous || "{}");
        const b = JSON.parse(update || "{}");
        if (typeof a !== "object" || typeof b !== "object" || !a || !b)
            return update;
        return JSON.stringify({ ...a, ...b });
    }
    catch {
        return update || previous;
    }
}
